import {
  useFormContext,
  type RegisterOptions,
  type FieldValues,
  type Path,
} from 'react-hook-form'
import styles from './Input.module.scss'

interface InputProps<T extends FieldValues> {
  name: Path<T>
  label?: string
  placeholder?: string
  required?: boolean
  disabled?: boolean
  readOnly?: boolean
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url'
  mode?: 'input' | 'textarea'
  rows?: number
  validation?: RegisterOptions<T>
  testId?: string
}

export const Input = <T extends FieldValues>({
  name,
  label,
  placeholder,
  required = false,
  disabled = false,
  readOnly = false,
  type = 'text',
  mode = 'input',
  rows = 4,
  validation,
  testId,
}: InputProps<T>) => {
  const {
    register,
    formState: { errors },
  } = useFormContext<T>()

  const error = errors[name]
  const errorMessage = error?.message as string | undefined

  const containerClassNames = [styles.container, !label && styles.noLabel]
    .filter(Boolean)
    .join(' ')

  const inputClassNames = [
    styles.input,
    mode === 'textarea' && styles.textarea,
    error && styles.inputError,
  ]
    .filter(Boolean)
    .join(' ')

  const registerOptions: RegisterOptions<T> = {
    required: required ? 'This field is required' : false,
    ...validation,
  }

  return (
    <div className={containerClassNames}>
      {label && (
        <label htmlFor={name} className={styles.label}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
      )}
      {mode === 'textarea' ? (
        <textarea
          id={name}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          rows={rows}
          className={inputClassNames}
          data-testid={testId || `input-${name}`}
          {...register(name, registerOptions)}
        />
      ) : (
        <input
          id={name}
          type={type}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          className={inputClassNames}
          data-testid={testId || `input-${name}`}
          {...register(name, registerOptions)}
        />
      )}
      {errorMessage && (
        <span
          className={styles.errorMessage}
          data-testid={testId ? `${testId}-error` : `input-${name}-error`}
        >
          {errorMessage}
        </span>
      )}
    </div>
  )
}
